package com.evoapps.evoshift.ui.user

import evoshift.composeapp.generated.resources.contract_owner_partner
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Icon
import androidx.compose.ui.Alignment
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import evoshift.composeapp.generated.resources.employee_below_minimum_hourly
import evoshift.composeapp.generated.resources.employee_minimum_top_up
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.market.payroll.PayrollFact
import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.TermsChange
import com.evoapps.evoshift.widgets.EvoDateField
import com.evoapps.evoshift.widgets.MissingYear
import com.evoapps.evoshift.domain.market.payroll.Payslip
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.finance.formatMoney
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoSwitchRow
import evoshift.composeapp.generated.resources.dialog_section_payroll_facts
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.contract_b2b
import evoshift.composeapp.generated.resources.contract_civil
import evoshift.composeapp.generated.resources.contract_employment
import evoshift.composeapp.generated.resources.contract_specific_task
import evoshift.composeapp.generated.resources.employee_contract_type
import evoshift.composeapp.generated.resources.payroll_commutes
import evoshift.composeapp.generated.resources.payroll_employer_cost
import evoshift.composeapp.generated.resources.payroll_estimate_title
import evoshift.composeapp.generated.resources.payroll_gross
import evoshift.composeapp.generated.resources.payroll_insured_elsewhere
import evoshift.composeapp.generated.resources.payroll_net
import evoshift.composeapp.generated.resources.payroll_student
import evoshift.composeapp.generated.resources.payroll_tax_relief
import evoshift.composeapp.generated.resources.payroll_under26
import evoshift.composeapp.generated.resources.payroll_voluntary_sickness
import evoshift.composeapp.generated.resources.payroll_over50
import evoshift.composeapp.generated.resources.payroll_long_service
import evoshift.composeapp.generated.resources.payroll_ppk
import evoshift.composeapp.generated.resources.payroll_ppk_reduced
import evoshift.composeapp.generated.resources.payroll_fact_commutes
import evoshift.composeapp.generated.resources.payroll_fact_ended
import evoshift.composeapp.generated.resources.payroll_fact_insured_elsewhere
import evoshift.composeapp.generated.resources.payroll_fact_long_service
import evoshift.composeapp.generated.resources.payroll_fact_off
import evoshift.composeapp.generated.resources.payroll_fact_on
import evoshift.composeapp.generated.resources.payroll_fact_over50
import evoshift.composeapp.generated.resources.payroll_fact_ppk
import evoshift.composeapp.generated.resources.payroll_fact_ppk_reduced
import evoshift.composeapp.generated.resources.payroll_fact_student
import evoshift.composeapp.generated.resources.payroll_fact_tax_relief
import evoshift.composeapp.generated.resources.payroll_fact_under26
import evoshift.composeapp.generated.resources.payroll_fact_voluntary_sickness
import evoshift.composeapp.generated.resources.payroll_rate_hourly
import evoshift.composeapp.generated.resources.payroll_rate_monthly
import evoshift.composeapp.generated.resources.payroll_student_until
import evoshift.composeapp.generated.resources.payroll_under26_until
import evoshift.composeapp.generated.resources.payroll_until_hint
import org.jetbrains.compose.resources.stringResource

/** Contracts a manager can pick (NOT_SET only describes older data). */
val SelectableContracts = SystemContractType.entries - SystemContractType.NOT_SET

@Composable
fun contractLabel(type: SystemContractType): String = stringResource(
    when (type) {
        SystemContractType.STANDARD_EMPLOYMENT, SystemContractType.NOT_SET -> Res.string.contract_employment
        SystemContractType.CIVIL_LAW_CONTRACT -> Res.string.contract_civil
        SystemContractType.SPECIFIC_TASK_CONTRACT -> Res.string.contract_specific_task
        SystemContractType.B2B_CONTRACTOR -> Res.string.contract_b2b
        SystemContractType.OWNER_PARTNER -> Res.string.contract_owner_partner
    }
)

@Composable
fun ContractDropdown(selected: SystemContractType, onSelected: (SystemContractType) -> Unit) {
    EvoDropdown(
        label = stringResource(Res.string.employee_contract_type),
        options = SelectableContracts,
        selected = selected.takeIf { it != SystemContractType.NOT_SET },
        onSelected = onSelected,
        optionLabel = { contractLabel(it) }
    )
}

/** The facts the Polish payroll needs, only those that matter for [contract]. */
@Composable
fun PayrollProfileSwitches(contract: SystemContractType, profile: PayrollProfile, onChange: (PayrollProfile) -> Unit) {
    val civil = contract == SystemContractType.CIVIL_LAW_CONTRACT
    val employment = contract == SystemContractType.STANDARD_EMPLOYMENT || contract == SystemContractType.NOT_SET
    // Own businesses invoice; the owner or a partner has no contract (SH-133): no payroll facts.
    if (contract == SystemContractType.B2B_CONTRACTOR || contract == SystemContractType.OWNER_PARTNER) return
    DialogSection(stringResource(Res.string.dialog_section_payroll_facts)) {
        Column {
            if (employment || civil) {
                EvoSwitchRow(
                    profile.under26,
                    { onChange(profile.copy(under26 = it, student = profile.student && it, under26Until = profile.under26Until.takeIf { _ -> it })) },
                    stringResource(Res.string.payroll_under26)
                )
                // SH-149: a known end (the 26th birthday): after it the relief stops by itself.
                if (profile.under26) {
                    KnownEndField(profile.under26Until, stringResource(Res.string.payroll_under26_until)) { onChange(profile.copy(under26Until = it)) }
                }
            }
            if (civil) {
                // A student status only exempts until 26.
                EvoSwitchRow(
                    profile.student,
                    { onChange(profile.copy(student = it, under26 = profile.under26 || it, studentUntil = profile.studentUntil.takeIf { _ -> it })) },
                    stringResource(Res.string.payroll_student)
                )
                if (profile.student) {
                    KnownEndField(profile.studentUntil, stringResource(Res.string.payroll_student_until)) { onChange(profile.copy(studentUntil = it)) }
                }
                EvoSwitchRow(profile.voluntarySickness, { onChange(profile.copy(voluntarySickness = it)) }, stringResource(Res.string.payroll_voluntary_sickness))
                EvoSwitchRow(
                    profile.insuredElsewhereAtMinimumWage,
                    { onChange(profile.copy(insuredElsewhereAtMinimumWage = it)) },
                    stringResource(Res.string.payroll_insured_elsewhere)
                )
            }
            EvoSwitchRow(profile.taxReliefRequested, { onChange(profile.copy(taxReliefRequested = it)) }, stringResource(Res.string.payroll_tax_relief))
            if (employment) {
                EvoSwitchRow(profile.commutes, { onChange(profile.copy(commutes = it)) }, stringResource(Res.string.payroll_commutes))
                // SH-57: sick pay days and the leave entitlement.
                EvoSwitchRow(profile.over50, { onChange(profile.copy(over50 = it)) }, stringResource(Res.string.payroll_over50))
                EvoSwitchRow(profile.longService, { onChange(profile.copy(longService = it)) }, stringResource(Res.string.payroll_long_service))
            }
            // SH-57: PPK where the pension insurance is due (not for a student's or an otherwise insured civil-law contract).
            if (employment || (civil && !profile.student && !profile.insuredElsewhereAtMinimumWage)) {
                EvoSwitchRow(profile.ppk, { onChange(profile.copy(ppk = it, ppkReducedRate = profile.ppkReducedRate && it)) }, stringResource(Res.string.payroll_ppk))
                if (profile.ppk) {
                    EvoSwitchRow(profile.ppkReducedRate, { onChange(profile.copy(ppkReducedRate = it)) }, stringResource(Res.string.payroll_ppk_reduced))
                }
            }
        }
    }
}

/** SH-149: the last day of a status with a known end; blank: none. */
@Composable
private fun KnownEndField(value: String?, label: String, onChange: (String?) -> Unit) {
    Column(Modifier.padding(start = 16.dp, bottom = 8.dp)) {
        EvoDateField(value = value.orEmpty(), onValueChange = { onChange(it.trim().ifEmpty { null }) }, label = label, missingYear = MissingYear.Upcoming)
        Text(stringResource(Res.string.payroll_until_hint), color = Color.White.copy(alpha = 0.75f), style = MaterialTheme.typography.bodySmall)
    }
}

/** SH-149: the short name of a payroll fact, for the history of changes. */
@Composable
fun payrollFactLabel(fact: PayrollFact): String = stringResource(
    when (fact) {
        PayrollFact.UNDER_26 -> Res.string.payroll_fact_under26
        PayrollFact.STUDENT -> Res.string.payroll_fact_student
        PayrollFact.TAX_RELIEF -> Res.string.payroll_fact_tax_relief
        PayrollFact.COMMUTES -> Res.string.payroll_fact_commutes
        PayrollFact.VOLUNTARY_SICKNESS -> Res.string.payroll_fact_voluntary_sickness
        PayrollFact.INSURED_ELSEWHERE -> Res.string.payroll_fact_insured_elsewhere
        PayrollFact.OVER_50 -> Res.string.payroll_fact_over50
        PayrollFact.LONG_SERVICE -> Res.string.payroll_fact_long_service
        PayrollFact.PPK -> Res.string.payroll_fact_ppk
        PayrollFact.PPK_REDUCED -> Res.string.payroll_fact_ppk_reduced
    }
)

/** SH-149: what changed on one day: the new pay and the facts switched on, off or ended. */
@Composable
fun termsChangeText(change: TermsChange, currency: String): String {
    val parts = mutableListOf<String>()
    when (val pay = change.pay) {
        is CompensationModel.Hourly -> parts += stringResource(Res.string.payroll_rate_hourly, formatMoney(pay.rate, currency))
        is CompensationModel.MonthlyFixed -> parts += stringResource(Res.string.payroll_rate_monthly, formatMoney(pay.baseSalary, currency))
        null -> Unit
    }
    change.facts.forEach { fact ->
        val name = payrollFactLabel(fact.fact)
        parts += stringResource(
            when {
                fact.knownEnd -> Res.string.payroll_fact_ended
                fact.on -> Res.string.payroll_fact_on
                else -> Res.string.payroll_fact_off
            },
            name
        )
    }
    return parts.joinToString("; ")
}

/**
 * Gross, take-home and employer cost of a month; [onDark] for the navy edit dialog. SH-57:
 * [factors] adds the minimum-wage top-up (in the gross) or the hourly-minimum warning.
 */
@Composable
fun PayEstimate(slip: Payslip, hours: Int, currency: String, onDark: Boolean, factors: MonthPayFactors? = null) {
    val text = if (onDark) Color.White else DeepNavy
    Column(Modifier.fillMaxWidth().padding(top = 12.dp)) {
        HorizontalDivider(color = text.copy(alpha = 0.2f))
        Text(
            stringResource(Res.string.payroll_estimate_title, hours),
            color = if (onDark) DarkGold else DeepNavy.copy(alpha = 0.7f),
            style = MaterialTheme.typography.labelMedium,
            modifier = Modifier.padding(vertical = 8.dp)
        )
        EstimateRow(stringResource(Res.string.payroll_gross), formatMoney(slip.gross, currency), text)
        factors?.takeIf { it.minimumWageTopUp > 0 }?.let {
            Text(
                stringResource(Res.string.employee_minimum_top_up, formatMoney(it.minimumPay, currency), formatMoney(it.minimumWageTopUp, currency)),
                color = text.copy(alpha = 0.8f),
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(vertical = 2.dp)
            )
        }
        EstimateRow(stringResource(Res.string.payroll_net), formatMoney(slip.net, currency), text, bold = true)
        EstimateRow(stringResource(Res.string.payroll_employer_cost), formatMoney(slip.employerCost, currency), text, bold = true)
        factors?.minimumHourlyRate?.let { minimum ->
            Row(Modifier.padding(top = 8.dp), verticalAlignment = Alignment.Top) {
                Icon(Icons.Default.Warning, null, tint = if (onDark) DarkGold else Color(0xFFD32F2F), modifier = Modifier.size(16.dp))
                Spacer(Modifier.width(6.dp))
                Text(
                    stringResource(Res.string.employee_below_minimum_hourly, formatMoney(minimum, currency)),
                    color = if (onDark) DarkGold else Color(0xFFD32F2F),
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }
    }
}

@Composable
private fun EstimateRow(label: String, value: String, color: Color, bold: Boolean = false) {
    Row(Modifier.fillMaxWidth().padding(vertical = 2.dp)) {
        Text(label, Modifier.weight(1f), color = color.copy(alpha = 0.8f), style = MaterialTheme.typography.bodyMedium)
        Text(value, color = color, style = MaterialTheme.typography.bodyMedium, fontWeight = if (bold) FontWeight.Bold else FontWeight.Normal)
    }
}
